import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import DrawerShell from '../components/DrawerShell';
import {
  Pill, Search, Plus, Trash2, Loader2, AlertTriangle, Package, Upload, LogOut,
  ArrowLeft, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const UOMS = ['tab', 'cap', 'syr', 'inj', 'drop', 'amp', 'sachet', 'tube', 'vial', 'ml', 'unit'];

/**
 * Lite inventory screen — visible to doctors and reception so they know
 * what's actually in stock, editable by admin + pharmacist. When pharmacy
 * mode is 'lite' this is the clinic's stock list; when it's 'full' the
 * legacy purchasing module owns inventory and this page is hidden.
 */
export default function InventoryPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || '').toLowerCase();
  const [canEdit, setCanEdit] = useState(role === 'admin' || role === 'pharmacist');

  // Fetch the merged capability — covers per-user `can_manage_inventory`
  // grants that the tenant admin gave non-pharmacist staff.
  useEffect(() => {
    if (canEdit) return; // already implicit for admin/pharmacist
    ax.get('/api/clinic-ops/me/capabilities')
      .then(({ data }) => { if (data?.can_manage_inventory) setCanEdit(true); })
      .catch(() => {});
  }, [canEdit]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [expiringFilter, setExpiringFilter] = useState(0);
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (expiringFilter > 0) params.set('expiring_in_days', expiringFilter);
      if (lowStockOnly) params.set('low_stock_only', 'true');
      const { data } = await ax.get(`/api/clinic-ops/inventory?${params.toString()}`);
      setRows(data || []);
    } catch (_) { setRows([]); }
    setLoading(false);
  }, [q, expiringFilter, lowStockOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  const total = rows.length;
  const lowStockCount = useMemo(
    () => rows.filter(r => Number(r.quantity) <= Number(r.low_stock_threshold ?? 5)).length,
    [rows],
  );

  const loadSample = async () => {
    if (!window.confirm('Load 68 sample medicines? Existing items stay; sample items are appended.')) return;
    try {
      const { data: sample } = await ax.get('/api/clinic-ops/inventory/sample');
      const r = await ax.post('/api/clinic-ops/inventory/bulk', { items: sample });
      toast.success(`Inserted ${r.data.inserted} medicines`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Bulk load failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this medicine from inventory?')) return;
    try {
      await ax.delete(`/api/clinic-ops/inventory/${id}`);
      refresh();
    } catch (_) { toast.error('Failed to delete'); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="inventory-page">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-700" />
            <span className="font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Medicine Inventory
            </span>
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              LITE
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="inv-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <span className="text-sm text-zinc-600">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="inv-logout">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <div className="bg-white border rounded-xl p-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by medicine name…" className="pl-8" data-testid="inv-search" />
            </div>
            <Select value={String(expiringFilter)} onValueChange={(v) => setExpiringFilter(Number(v))}>
              <SelectTrigger className="w-44" data-testid="inv-expiry-filter">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <SelectValue placeholder="Expiry filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All expiry dates</SelectItem>
                <SelectItem value="30">Expiring ≤ 30 days</SelectItem>
                <SelectItem value="90">Expiring ≤ 90 days</SelectItem>
                <SelectItem value="180">Expiring ≤ 6 months</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-sm text-zinc-700">
              <input type="checkbox" checked={lowStockOnly}
                onChange={(e) => setLowStockOnly(e.target.checked)}
                data-testid="inv-low-stock-filter" />
              Low stock only
            </label>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-zinc-500">
                {total} item{total !== 1 ? 's' : ''}
                {lowStockCount > 0 && (
                  <span className="ml-2 text-amber-700">· {lowStockCount} low</span>
                )}
              </span>
              {canEdit && (
                <>
                  <Button variant="outline" size="sm" onClick={loadSample} data-testid="inv-load-sample">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Load sample data
                  </Button>
                  <MedicineDialog onDone={refresh} />
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b text-left text-zinc-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Medicine</th>
                  <th className="px-3 py-2 font-medium">Batch</th>
                  <th className="px-3 py-2 font-medium">Expiry</th>
                  <th className="px-3 py-2 font-medium text-right">Qty</th>
                  <th className="px-3 py-2 font-medium">UOM</th>
                  <th className="px-3 py-2 font-medium text-right">MRP ₹</th>
                  {canEdit && <th className="px-3 py-2 font-medium text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={canEdit ? 7 : 6} className="px-3 py-8 text-center text-zinc-500">
                    <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
                  </td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={canEdit ? 7 : 6} className="px-3 py-12 text-center text-zinc-400">
                    <Pill className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No medicines in stock yet.</p>
                    {canEdit && (
                      <p className="text-xs mt-1">Use <b>+ Add medicine</b> or <b>Load sample data</b> to start.</p>
                    )}
                  </td></tr>
                ) : rows.map(m => {
                  const low = Number(m.quantity) <= Number(m.low_stock_threshold ?? 5);
                  return (
                    <tr key={m.id} className="border-b last:border-0 hover:bg-zinc-50"
                      data-testid={`inv-row-${m.id}`}>
                      <td className="px-3 py-2 text-zinc-900">{m.name}</td>
                      <td className="px-3 py-2 text-zinc-600">{m.batch || '—'}</td>
                      <td className="px-3 py-2 text-zinc-600">{m.expiry || '—'}</td>
                      <td className={`px-3 py-2 text-right font-medium ${low ? 'text-amber-700' : 'text-zinc-900'}`}>
                        {low && <AlertTriangle className="h-3 w-3 inline mr-0.5" />}
                        {m.quantity}
                      </td>
                      <td className="px-3 py-2 text-zinc-600 uppercase text-xs">{m.uom}</td>
                      <td className="px-3 py-2 text-right text-zinc-700">{m.mrp || 0}</td>
                      {canEdit && (
                        <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                          <MedicineDialog initial={m} onDone={refresh} />
                          <Button variant="ghost" size="sm" onClick={() => remove(m.id)}
                            className="text-red-700" data-testid={`inv-delete-${m.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function MedicineDialog({ initial = null, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {initial ? (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid={`inv-edit-${initial.id}`}>Edit</Button>
      ) : (
        <Button size="sm" onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="inv-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add medicine
        </Button>
      )}
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow={initial ? 'Edit Stock' : 'New SKU'}
        title={initial ? 'Edit medicine' : 'Add medicine'}
        subtitle={initial ? `Updating ${initial.name}` : 'Add a new SKU to the dispensary'}
        accent="#1d4ed8"
        icon={<Pill className="h-5 w-5" />}
        width="md"
        testId={initial ? `inv-edit-drawer-${initial.id}` : 'inv-add-drawer'}
      >
        <MedicineForm initial={initial} onClose={() => setOpen(false)} onDone={onDone} />
      </DrawerShell>
    </>
  );
}

function MedicineForm({ initial, onClose, onDone }) {
  const [name, setName] = useState(initial?.name || '');
  const [batch, setBatch] = useState(initial?.batch || '');
  const [expiry, setExpiry] = useState(initial?.expiry || '');
  const [quantity, setQuantity] = useState(initial?.quantity ?? '');
  const [uom, setUom] = useState(initial?.uom || 'tab');
  const [mrp, setMrp] = useState(initial?.mrp ?? '');
  const [gstPercent, setGstPercent] = useState(initial?.gst_percent ?? 12);
  const [lowStock, setLowStock] = useState(initial?.low_stock_threshold ?? 5);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        batch: batch || '',
        expiry: expiry || '',
        quantity: Number(quantity) || 0,
        uom,
        mrp: Number(mrp) || 0,
        gst_percent: Number(gstPercent) || 12,
        low_stock_threshold: Number(lowStock) || 5,
      };
      if (initial?.id) {
        await ax.patch(`/api/clinic-ops/inventory/${initial.id}`, body);
        toast.success('Updated');
      } else {
        await ax.post('/api/clinic-ops/inventory', body);
        toast.success('Added');
      }
      onClose();
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
    setSaving(false);
  };

  return (
    <>
      <div className="p-5 space-y-3">
        <div className="space-y-1.5">
          <Label>Medicine name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="inv-form-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Batch</Label>
            <Input value={batch} onChange={(e) => setBatch(e.target.value)}
              placeholder="e.g. B23-481" data-testid="inv-form-batch" />
          </div>
          <div className="space-y-1.5">
            <Label>Expiry</Label>
            <Input type="month" value={expiry?.slice(0, 7) || ''}
              onChange={(e) => setExpiry(e.target.value)}
              data-testid="inv-form-expiry" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Quantity</Label>
            <Input type="number" min="0" value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="inv-form-qty" />
          </div>
          <div className="space-y-1.5">
            <Label>UOM</Label>
            <Select value={uom} onValueChange={setUom}>
              <SelectTrigger data-testid="inv-form-uom"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UOMS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>MRP ₹</Label>
            <Input type="number" min="0" step="0.01" value={mrp}
              onChange={(e) => setMrp(e.target.value)}
              data-testid="inv-form-mrp" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>GST %</Label>
            <Select value={String(gstPercent)} onValueChange={(v) => setGstPercent(Number(v))}>
              <SelectTrigger data-testid="inv-form-gst"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0% (Exempt)</SelectItem>
                <SelectItem value="5">5% (Essential / life-saving)</SelectItem>
                <SelectItem value="12">12% (General formulations)</SelectItem>
                <SelectItem value="18">18% (Specialty / cosmetic)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Low-stock alert below</Label>
            <Input type="number" min="0" value={lowStock}
              onChange={(e) => setLowStock(e.target.value)}
              data-testid="inv-form-low" />
          </div>
        </div>
      </div>
      <div className="px-5 py-3 border-t bg-white flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving}
          className="bg-blue-700 hover:bg-blue-800" data-testid="inv-form-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save
        </Button>
      </div>
    </>
  );
}
